import {
  CapsuleGeometry,
  BoxGeometry,
  CylinderGeometry,
  Mesh,
  SphereGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  Object3D,
} from "three";
import Util from "./utils.js";

const BasicObjectType = {
  Box: 0,
  Sphere: 1,
  Capsule: 2,
  Cylinder: 3,
  Plane: 4,
  Quad: 5,
};

class Create3DObject {
  constructor() {
    this.objectArray = [];
    this.boxCount = 0;
    this.sphereCount = 0;
    this.cylinderCount = 0;
    this.quadCount = 0;
    this.planeCount = 0;
    this.capsuleCount = 0;
  }

  /**
   * 根据类型创建3D对象
   * @param {*} type
   * @param {*} position
   * @param {*} rotation
   * @param {*} scale
   * @param {String} name 对象名称，默认为null，还原时使用
   * @param {String} modelId 模型id，默认为null，还原时使用
   * @param {*} callback 回调函数，带一个参数，参数为创建的对象
   * @returns
   */
  createObject(
    type,
    position,
    rotation,
    scale,
    name = "",
    modelId = null,
    callback = null,
    needRecord = false
  ) {
    let object = null;
    switch (type) {
      case BasicObjectType.Box:
        object = new Mesh(
          new BoxGeometry(1, 1, 1),
          new MeshStandardMaterial({ color: 0xffffff })
        );
        object.name =
          this.boxCount === 0 ? "Box" : "Box(" + this.boxCount + ")";
        this.boxCount++;
        break;
      case BasicObjectType.Sphere:
        object = new Mesh(
          new SphereGeometry(1, 32, 32),
          new MeshStandardMaterial({ color: 0xffffff })
        );
        object.name =
          this.sphereCount === 0
            ? "Sphere"
            : "Sphere(" + this.sphereCount + ")";
        this.sphereCount++;
        break;
      case BasicObjectType.Cylinder:
        object = new Mesh(
          new CylinderGeometry(1, 1, 1, 32),
          new MeshStandardMaterial({ color: 0xffffff })
        );
        object.name =
          this.cylinderCount === 0
            ? "Cylinder"
            : "Cylinder(" + this.cylinderCount + ")";
        this.cylinderCount++;
        break;
      case BasicObjectType.Quad:
        object = new Mesh(
          new PlaneGeometry(1, 1),
          new MeshStandardMaterial({ color: 0xffffff })
        );
        object.name =
          this.quadCount === 0 ? "Quad" : "Quad(" + this.quadCount + ")";
        this.quadCount++;
        break;
      case BasicObjectType.Plane:
        object = new Mesh(
          new PlaneGeometry(1, 1, 10, 10),
          new MeshStandardMaterial({ color: 0xffffff })
        );
        object.name =
          this.planeCount === 0 ? "Plane" : "Plane(" + this.planeCount + ")";
        this.planeCount++;
        break;
      case BasicObjectType.Capsule:
        object = new Mesh(
          new CapsuleGeometry(1, 1, 32, 32),
          new MeshStandardMaterial({ color: 0xffffff })
        );
        object.name =
          this.capsuleCount === 0
            ? "Capsule"
            : "Capsule(" + this.capsuleCount + ")";
        this.capsuleCount++;
        break;
    }
    if (name && name.length > 0) object.name = name;
    object.material.name = object.name;

    object.castShadow = true; // 物体投射阴影
    object.receiveShadow = true; // 地面接收阴影

    // 增加一个父对象
    let parent = new Object3D();
    parent.add(object);
    parent.name = object.name;
    parent._ObjectType = type;
    if (needRecord) this.objectArray.push(parent);

    parent.position.set(position.x, position.y, position.z);
    parent.quaternion.fromArray(rotation);
    parent.scale.set(scale.x, scale.y, scale.z);

    this._makeSomeProperties(parent, modelId);

    if (callback) callback(parent);
    return parent;
  }

  /**
   * 设置一些模型的属性，以便与其它模型操作统一
   */
  _makeSomeProperties(obj, srcModelId = null) {
    if (!obj) return;

    obj.userData.modelFrom = "basicObj";

    const modelId = srcModelId ? srcModelId : obj.uuid;
    obj.modelId = modelId;
    obj.nodeId = modelId + "|" + "-1";
    obj._root = obj;

    obj.children[0].modelId = modelId;
    obj.children[0].nodeId = modelId + "|" + "0";
    obj.children[0]._root = obj;
    obj.children[0].material.modelId = modelId;
    obj.children[0].material.matId = obj.children[0].nodeId;
  }

  deleteByModelId(modelId) {
    for (var i = 0; i < this.objectArray.length; i++) {
      if (this.objectArray[i].modelId === modelId) {
        //Util.disposeFun(this.objectArray[i]);
        this.objectArray.splice(i, 1);
        break;
      }
    }
  }

  /**
   * 保存创建的3D模型数据
   */
  save() {
    const objectsInfo = [];

    for (var i = 0; i < this.objectArray.length; i++) {
      const obj = this.objectArray[i];
      const temp = {
        type: obj._ObjectType,
        pos: obj.position.clone(),
        quat: obj.quaternion.clone(),
        scale: obj.scale.clone(),
        modelId: obj.modelId,
        name: obj.name,
        visible: obj._delete ? "delete" : obj.visible ? "visible" : "hide",
      };
      objectsInfo.push(temp);
    }

    return objectsInfo;
  }

  /**
   * 还原创建的3D模型数据
   * @param {*} info
   */
  restore(info, addToSceneCB) {
    if (!info || !Array.isArray(info)) return;
    for (var i = 0; i < info.length; i++) {
      let obj = this.createObject(
        info[i].type,
        info[i].pos,
        info[i].quat,
        info[i].scale,
        info[i].name,
        info[i].modelId,
        addToSceneCB,
        true
      );
      if (info[i].visible === "delete") {
        obj._delete = true;
        obj.scale.set(0, 0, 0);
        obj.visible = false;
      } else if (info[i].visible === "hide") {
        obj.visible = false;
      } else {
        obj.visible = true;
      }
    }
  }

  clear() {
    for (var i = 0; i < this.objectArray.length; i++) {
      Util.disposeFun(this.objectArray[i]);
    }
    this.objectArray = [];

    this.boxCount = 0;
    this.sphereCount = 0;
    this.cylinderCount = 0;
    this.quadCount = 0;
    this.planeCount = 0;
    this.capsuleCount = 0;
  }
}

export { Create3DObject, BasicObjectType };
